// simpause.ts — the SP story-modal SEQUENCER + sim-pause gate.
//
// In the C++ every story modal (Ecom / CutScene / the ObjectivesForm) BLOCKS
// inside a nested modal event loop at its call site, with gsim.Pause held
// around it:
//   - EcomAction::Perform        (TriggerActions.cpp:519-525)
//   - CutScene()                 (CutScene.cpp:40-46)
//   - the ShowObjectives callers (InputUI.cpp:260-264, :352/:450 — and the
//     trigger arm, TriggerActions.cpp:1127-1157)
// Two consequences the dissolved-modal pump must reproduce:
//
//  1. SEQUENCING — a single trigger pass that raises several story modals
//     (s_01's mission-load chain: T 1:0's Olstrom ecom -> the briefing ->
//     T 1:1's Jana ecom; T 1:4's two back-to-back tutorial ecoms) shows them
//     ONE AT A TIME, in Perform order — the C++ literally suspends the pass
//     inside each action until its modal is dismissed. Without sequencing the
//     pump stacks the forms from one pass: several PAINT AT ONCE (two ecoms on
//     screen) and they unwind in REVERSE order (Jana's "Congratulations"
//     showing before the briefing at M1 start) — user report 2026-06-12.
//     WASM ground truth (out/web-shell/gt-wasm-*): the real client shows
//     1040(Olstrom) -> 1025(briefing) -> 1040(Jana), one at a time.
//
//  2. PAUSE — the sim must not advance from the first modal's raise until the
//     LAST one is dismissed. In the C++ each action holds gsim.Pause while its
//     modal is up, and BETWEEN modals of one pass no update can run anyway
//     (the pass itself is still on the call stack). Simulation::Pause is a
//     plain bool (Simulation.cpp:1223-1226), so the gate refcounts holders and
//     re-asserts on release; each queued modal holds from ENQUEUE to its own
//     dismissal, so the count stays >= 1 across the whole chain. WASM ground
//     truth: the entire s_01 intro runs at updateCount 1.
//
// Callers must run any embedder `done` continuation (which may contain the
// C++ caller's own direct Pause(false), e.g. the GameSession's
// host.ShowObjectives wiring) BEFORE the queue's `next()`, so the release
// re-asserts over it while other holders remain.

interface SimPauseSeam {
  Pause(fPause: boolean): void;
}

const counts = new WeakMap<SimPauseSeam, number>();

/** gsim.Pause(true) — one story-modal hold over `sim`. */
export function SimPauseHold(sim: SimPauseSeam | null | undefined): void {
  if (sim === null || sim === undefined) return;
  counts.set(sim, (counts.get(sim) ?? 0) + 1);
  sim.Pause(true);
}

/** gsim.Pause(false) — one hold released; the sim resumes only when the last
 *  holder releases (re-asserts Pause(true) otherwise). */
export function SimPauseRelease(sim: SimPauseSeam | null | undefined): void {
  if (sim === null || sim === undefined) return;
  const c = Math.max(0, (counts.get(sim) ?? 0) - 1);
  counts.set(sim, c);
  sim.Pause(c > 0);
}

// ---- the per-session FIFO (keyed on the session's MultiFormMgr) -------------

/** The slice of ShellEnv the sequencer needs (every story-modal env has it). */
export interface StoryModalEnv {
  mfrmm: object; // gpmfrmm (the session key)
  sim: SimPauseSeam | null; // gsim
  fMultiplayer: boolean; // gfMultiplayer
}

interface QueueState {
  busy: boolean;
  q: (() => void)[];
}

const queues = new WeakMap<object, QueueState>();

/** Clear this session's story-modal FIFO (drop any queued entries; mark not-busy). Used before a TERMINAL
 *  modal — the MP game-over win/lose SUMMARY — so it can NEVER be queued behind an in-game story modal
 *  (briefing / intro E-Com) that failed to release the FIFO. A leaked `busy` would otherwise swallow the
 *  game-over entirely: the player is left a deselected observer with no summary and the game never ends
 *  (its "end the game" runs inside the summary's dismiss callback). The game is concluding, so dropping any
 *  still-pending in-game modal is correct. No-op if the FIFO is already free. */
export function ResetStoryModals(env: StoryModalEnv): void {
  const st = queues.get(env.mfrmm);
  if (st !== undefined) {
    st.busy = false;
    st.q.length = 0;
  }
}

/** Serialize a story modal behind any already-showing/queued ones (FIFO, the
 *  C++ blocking order). `show(next)` must present the modal and call `next()`
 *  exactly once when it is FULLY dismissed — after the caller's own done
 *  continuation. The SP pause is held from enqueue until that next() call.
 *  fPause=false skips the pause (the MP arms; CutScene's fPauseSimulation). */
export function EnqueueStoryModal(
  env: StoryModalEnv,
  fPause: boolean,
  show: (next: () => void) => void,
): void {
  let st = queues.get(env.mfrmm);
  if (st === undefined) {
    st = { busy: false, q: [] };
    queues.set(env.mfrmm, st);
  }
  const qst = st;
  const fHold = fPause && !env.fMultiplayer;
  if (fHold) SimPauseHold(env.sim);
  const entry = (): void => {
    show(() => {
      if (fHold) SimPauseRelease(env.sim);
      const next = qst.q.shift();
      if (next !== undefined) {
        next(); // show the next queued story modal (the C++ pass resumes)
      } else {
        qst.busy = false;
      }
    });
  };
  if (qst.busy) {
    qst.q.push(entry);
  } else {
    qst.busy = true;
    entry();
  }
}
